/**
 * PDF Viewer Component
 *
 * Wraps PDF.js viewer in an iframe with PostMessage integration for communication
 * between the React app and the PDF viewer.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useServiceContext } from '../../root'
import {
  CHECK_INTERVAL,
  CONVERSION_STATUS,
  EVENTS,
  type PdfViewerProps,
  type PdfViewerMessageEvent,
  type ConversionStatusResponse
} from './types'
import './PdfViewer.css'

export const PdfViewer: React.FC<PdfViewerProps> = (props) => {
  const context = useServiceContext()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const nextCheckRef = useRef<NodeJS.Timeout | null>(null)
  const mountedRef = useRef(true)
  const isInitialMountRef = useRef(true)
  // Use ref for OCR URL since it's tied to the file, not the PDF conversion URL
  const ocrFileUrlRef = useRef<string | null>(null)
  // Use refs for original URLs to persist through any state resets
  const originalUrlRef = useRef<string | null>(null)
  const originalIframeUrlRef = useRef<string | null>(null)

  // Simple state without complex derived values
  const [url, setUrl] = useState<string | null>(null)
  const [iframeUrl, setIframeUrl] = useState<string | null>(null)
  const [iframeUrlCurrent, setIframeUrlCurrent] = useState<string | null>(null)
  const [ocrFileUrl, setOcrFileUrl] = useState<string | null>(null)
  const [originalUrl, setOriginalUrl] = useState<string | null>(null)
  const [originalIframeUrl, setOriginalIframeUrl] = useState<string | null>(null)
  const [showOverlay, setShowOverlay] = useState(true)
  const [showLoader, setShowLoader] = useState(false)
  const [showMessage, setShowMessage] = useState(false)
  const [showDownload, setShowDownload] = useState(false)
  const [loaderStatus, setLoaderStatus] = useState<string | null>(null)
  const [statusParams, setStatusParams] = useState<Record<string, any>>({})
  const [backgroundImage, setBackgroundImage] = useState<string>('')
  const [hasFailed, setHasFailed] = useState(false)

  // Calculate viewer URL once
  const viewerUrl = useRef<string>('')

  useEffect(() => {
    const { config } = context
    // buildId comes from build-id attribute which matches build-time attribute
    // Backend uses build-time to determine viewer filename: viewer-${BT}.html
    const buildId = config.buildId || ''

    let uiUrl: string

    // Check if config values contain placeholders (development mode)
    const hasPlaceholders =
      config.apiUrl.includes('domain_name_placeholder') ||
      config.apiUrl.includes('app_name_placeholder') ||
      config.cdnUrl.includes('cdn_domain_placeholder')

    if (hasPlaceholders) {
      // In development with placeholders, use current origin
      uiUrl = window.location.origin
    } else {
      // In production, viewer.html should be served from backend root (not CDN)
      // This ensures proper CORS handling for .mjs module files
      // Legacy working case: https://rcm-sandbox-ecs.imedidata.net/viewer-{buildId}.html
      if (config.apiUrl) {
        // Extract backend base URL from apiUrl
        // Example: "https://rcm-sandbox-ecs.imedidata.net/apps/edge-rcm/be/api"
        //       -> "https://rcm-sandbox-ecs.imedidata.net"
        const lastIndex = config.apiUrl.lastIndexOf('/')
        const secondLastIndex = config.apiUrl.lastIndexOf('/', lastIndex - 1)
        const contextPathUriName = config.apiUrl.substring(secondLastIndex + 1, lastIndex)
        const backendUrl = config.apiUrl.replace('/apps/' + contextPathUriName + '/be', '')
        // Add protocol if missing
        uiUrl = backendUrl.startsWith('http') ? backendUrl : 'https://' + backendUrl
        // Don't append /apps/.../be - viewer.html is at root
      } else {
        // Fallback to current origin
        uiUrl = window.location.origin
      }
    }

    viewerUrl.current = uiUrl + '/viewer-' + buildId + '.html'

    // Set font fallback
    const policies = context.service.auth.getPolicies()
    localStorage.setItem('isForgeFontFallbackEnabled', String(policies.forgeFontFallbackHandlingForWindows))
  }, [context])

  // Helper functions
  const getDisableFontFaceParam = () => {
    const isIE = !!(document as any).documentMode
    return isIE ? 'disableFontFace=true&' : ''
  }

  const getDisablePrintParam = () => {
    return props.disablePrint ? 'disablePrinter=true&' : ''
  }

  const getShowOCRPdfParam = () => {
    return props?.file?.canShowOcrPdf ? 'showOCRPdf=true&' : ''
  }

  const getDefaultIframeUrl = () => {
    const disablePrintParam = getDisablePrintParam()
    const showOcrSwitchParam = getShowOCRPdfParam()
    const localeParam = 'locale=' + context.service.auth.getCurrentLang()
    const disableFontFaceParam = getDisableFontFaceParam()

    return viewerUrl.current + '#' + disableFontFaceParam + disablePrintParam + localeParam + '&' + showOcrSwitchParam
  }

  const buildIframeUrl = (pdfUrl: string, isOcr = false) => {
    const disableFontFaceParam = getDisableFontFaceParam()
    const disablePrintParam = getDisablePrintParam()
    const showOcrSwitchParam = getShowOCRPdfParam()
    const localeParam = 'locale=' + context.service.auth.getCurrentLang()
    const encodedUrl = encodeURIComponent(pdfUrl)
    const trusted = context.service.filter.trusted
    const ocrParam = isOcr ? 'isOCRChecked=true' : ''

    return (
      viewerUrl.current +
      ('?' + 'file=' + trusted(encodedUrl)) +
      ('#' +
        disableFontFaceParam +
        disablePrintParam +
        localeParam +
        '&' +
        showOcrSwitchParam +
        (ocrParam ? ocrParam : ''))
    )
  }

  const setIframeSrc = (newUrl: string) => {
    if (iframeRef.current && newUrl) {
      // Force reload by setting src attribute directly (more reliable than contentWindow.location.replace)
      iframeRef.current.src = newUrl
      setIframeUrlCurrent(newUrl)

      // Post message after a small delay to ensure iframe has started loading
      setTimeout(() => {
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({
              key: 'isForgeFontFallbackEnabled-1',
              data: localStorage.getItem('isForgeFontFallbackEnabled')
            }),
            '*'
          )
        }
      }, 100)
    }
  }

  const finishLoading = () => {
    setShowOverlay(false)
    setShowLoader(false)
    setShowDownload(false)
    setShowMessage(false)
  }

  const displayError = (msg: string, params?: Record<string, any>) => {
    setShowMessage(true)
    setLoaderStatus(msg)
    setStatusParams(params || {})
    setShowLoader(false)
    setHasFailed(true)
  }

  const onLoadingSuccess = (pdfUrl: string) => {
    const checkmateProm = context.service.checkmate.isCheckmated()
      ? context.service.checkmate.initializeConnectionWithDirectRCM()
      : Promise.resolve()

    checkmateProm
      .then(() => {
        if (pdfUrl) {
          const newIframeUrl = buildIframeUrl(pdfUrl)
          setUrl(pdfUrl)
          setIframeUrl(newIframeUrl)
          setOriginalUrl(pdfUrl)
          setOriginalIframeUrl(newIframeUrl)
          // Also store in refs for persistence
          originalUrlRef.current = pdfUrl
          originalIframeUrlRef.current = newIframeUrl
          setIframeSrc(newIframeUrl)
        }

        if (typeof props.src === 'object' && props.src !== null && typeof props.src.callback === 'function') {
          props.src.callback(pdfUrl)
        }
      })
      .catch((err) => {
        console.error('❌ PdfViewer checkmate error:', err)
        displayError('PDF_VIEWER_CONNECTION_FAILURE_MSG')
      })
  }

  const onLoadingFailure = (data?: any) => {
    const errorData = data?.data || data

    if (errorData?.message) {
      displayError('ERROR_ERROR', { error: errorData.message })
    } else {
      displayError('ERROR_IN_GENERATING_PROCESS', {})
    }

    if (props.hasDownloaded && typeof props.onLastPage === 'function') {
      props.onLastPage()
    }
  }

  const checkConversionStatus = (uuid: string) => {
    if (!mountedRef.current) return

    nextCheckRef.current = setTimeout(() => {
      context.api.files
        .checkConversionStatus(uuid)
        .then((data: any) => {
          if (!mountedRef.current) return

          if (data) {
            const conversionState = data.conversionState || data.status
            switch (conversionState) {
              case CONVERSION_STATUS.CONVERTED_OK:
              case 'CONVERTED_OK':
                const convertedUrl = data.s3Url || data.url
                if (convertedUrl) {
                  onLoadingSuccess(convertedUrl)
                }
                break
              case CONVERSION_STATUS.CONVERTING:
              case 'CONVERTING':
                checkConversionStatus(uuid)
                break
              case CONVERSION_STATUS.FAILED:
              case 'FAILED':
                onLoadingFailure(data)
                break
            }
          } else {
            onLoadingFailure()
          }
        })
        .catch((data: any) => {
          if (mountedRef.current) {
            onLoadingFailure(data)
          }
        })
    }, CHECK_INTERVAL)
  }

  const loadPdf = () => {
    // Always show overlay, loader, and thumbnail as soon as loading starts (legacy behavior)
    setShowOverlay(true)
    setShowLoader(true)
    setShowMessage(true)
    setLoaderStatus('PDF_VIEWER_WAIT_FOR_PDF_GENERATION_MSG')
    setShowDownload(true)
    setHasFailed(false)

    // Handle string src (legacy compatibility)
    if (typeof props.src === 'string') {
      const pdfUrl = props.src
      if (pdfUrl) {
        onLoadingSuccess(pdfUrl)
      }
      return
    }

    // Handle object src with service
    if (!props.src || typeof props.src !== 'object' || !props.src.service) {
      return
    }

    const request = props.src.request || props.src.service()

    request
      .then((data: any) => {
        if (!mountedRef.current) return

        if (data && typeof data.url === 'string') {
          onLoadingSuccess(data.url)
        } else if (data.uuidToCheckState) {
          checkConversionStatus(data.uuidToCheckState)
        } else {
          onLoadingFailure(data)
        }
      })
      .catch((data: any) => {
        if (mountedRef.current) {
          onLoadingFailure(data)
        }
      })
  }

  // Handle PostMessage events
  const handleFrameMessage = (e: MessageEvent) => {
    const evt = e as PdfViewerMessageEvent

    if (!evt?.origin || !evt?.data || !evt.data.pdfViewer || !evt.data.pdfUrl) {
      return
    }

    // Allow messages if URL matches OR if we're in LOADING/RENDERING state and url is not set yet
    // OR if it's a special event type that doesn't need URL validation
    if (evt.data.pdfUrl !== url) {
      const isLoadingEvent =
        evt.data.pdfViewer === EVENTS.LOADING ||
        evt.data.pdfViewer === EVENTS.LOADING_FILE ||
        evt.data.pdfViewer === EVENTS.DONE_RENDERING_FILE

      const isBypassEvent =
        evt.data.pdfViewer === EVENTS.OCR_TOGGLE_STATE_CHANGE ||
        evt.data.pdfViewer === EVENTS.BEFORE_PRINT ||
        evt.data.pdfViewer === EVENTS.VIEWED_IN_BROWSER_TAB ||
        evt.data.pdfViewer === EVENTS.LAST_PAGE

      if (isLoadingEvent && !url) {
        setUrl(evt.data.pdfUrl)
        // Also set as original URL if not already set
        if (!originalUrl) {
          setOriginalUrl(evt.data.pdfUrl)
        }
      } else if (isBypassEvent) {
        // Allow bypass events regardless of URL
      } else {
        return
      }
    }

    switch (evt.data.pdfViewer) {
      case EVENTS.LOADING:
      case EVENTS.LOADING_FILE:
        // Always show overlay, loader, and thumbnail during loading (legacy behavior)
        setShowOverlay(true)
        setShowLoader(true)
        setShowMessage(true)
        setShowDownload(true)
        if (url && typeof props.src === 'object' && props.src !== null && typeof props.src.callback === 'function') {
          props.src.callback(url)
        }
        break

      case EVENTS.ERROR_LOADING:
        setShowLoader(false)
        setShowMessage(false)
        setShowOverlay(false)
        break

      case EVENTS.DONE_RENDERING_FILE:
        finishLoading()
        break

      case EVENTS.BEFORE_PRINT:
        if (props.printLog?.fsiId) {
          context.api.files.logPrint(props.printLog.fsiId)
        } else if (props.file?.id) {
          context.api.files.logPrint(props.file.id)
        }
        break

      case EVENTS.LAST_PAGE:
        if (typeof props.onLastPage === 'function') {
          props.onLastPage()
        }
        break

      case EVENTS.VIEWED_IN_BROWSER_TAB:
        if (props.file?.id) {
          context.api.files.logDocViewedInBrowserTab(props.file.id)
        }
        break

      case EVENTS.OCR_TOGGLE_STATE_CHANGE:
        const currentOcrUrl = ocrFileUrlRef.current || ocrFileUrl
        if (evt.data.isChecked) {
          if (!currentOcrUrl) {
            console.warn('OCR toggle clicked but OCR URL not available yet')
            break
          }
          const ocrUrl = buildIframeUrl(currentOcrUrl, true)
          setUrl(currentOcrUrl)
          setIframeUrl(ocrUrl)
          queueMicrotask(() => {
            setIframeSrc(ocrUrl)
          })
        } else {
          // Toggle back to original (saved in onLoadingSuccess)
          // Use refs as primary source, fall back to state
          const savedOriginalUrl = originalUrlRef.current || originalUrl
          const savedOriginalIframeUrl = originalIframeUrlRef.current || originalIframeUrl

          if (!savedOriginalUrl || !savedOriginalIframeUrl) {
            console.warn('Cannot switch back - original URL not saved')
            // Fallback to default iframe
            const defaultUrl = getDefaultIframeUrl()
            setIframeSrc(defaultUrl)
            break
          }
          setUrl(savedOriginalUrl)
          setIframeUrl(savedOriginalIframeUrl)
          queueMicrotask(() => {
            setIframeSrc(savedOriginalIframeUrl)
          })
        }
        break
    }
  }

  // Initialize default iframe on first render
  useEffect(() => {
    // Set default iframe src first
    const defaultUrl = getDefaultIframeUrl()
    if (iframeRef.current) {
      setIframeSrc(defaultUrl)
    }
  }, [])

  // originalUrl and originalIframeUrl are set in onLoadingSuccess, not here
  // This ensures we save the processed iframe URL, not just props.src

  // Mount effect
  useEffect(() => {
    // Add event listener
    window.addEventListener('message', handleFrameMessage)

    // Set thumbnail background
    if (props.thumbnailId) {
      const bgImage =
        context.constant.file.thumbnailUrl +
        props.thumbnailId +
        '?size=256&tenantUuid=' +
        context.service.auth.getTenant()
      setBackgroundImage(bgImage)
    }

    // Fetch OCR URL
    if (props?.file?.canShowOcrPdf && props?.file?.id) {
      context.api.files
        .getOCRVersionFileUrl(String(props.file.id), props.file.versionId)
        .then((data) => {
          if (data?.url && mountedRef.current) {
            ocrFileUrlRef.current = data.url
            setOcrFileUrl(data.url)
          }
        })
        .catch((err) => {
          console.error('Failed to load OCR URL:', err)
        })
    }

    // Load PDF if src provided
    if (props.src) {
      loadPdf()
    }

    return () => {
      mountedRef.current = false
      window.removeEventListener('message', handleFrameMessage)
      if (nextCheckRef.current) {
        clearTimeout(nextCheckRef.current)
      }
    }
  }, []) // Only run once on mount

  // Handle src changes (like componentDidUpdate in original)
  useEffect(() => {
    // Skip on initial mount - only run when props.src actually changes (like componentDidUpdate)
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }

    if (props.src) {
      // Reset state and reload (but keep ocrFileUrl - it's tied to the file, not the PDF URL)
      setUrl(null)
      setIframeUrl(null)
      setOriginalUrl(null)
      setOriginalIframeUrl(null)
      setHasFailed(false)
      // DO NOT reset ocrFileUrlRef or ocrFileUrl - they're file metadata, not conversion-specific
      if (nextCheckRef.current) {
        clearTimeout(nextCheckRef.current)
      }

      // Set default iframe then load new PDF
      const defaultUrl = getDefaultIframeUrl()
      setIframeSrc(defaultUrl)

      setTimeout(() => {
        if (mountedRef.current) {
          loadPdf()
        }
      }, 100)
    }
  }, [props.src])

  // Handle download function changes
  useEffect(() => {
    if (props.downloadFunction) {
      setShowDownload(true)
    }
  }, [props.downloadFunction])

  // Handle hasDownloaded changes
  useEffect(() => {
    // Just track the prop change, used in onLoadingFailure
  }, [props.hasDownloaded])

  // Handle thumbnailId changes
  useEffect(() => {
    if (props.thumbnailId) {
      const bgImage =
        context.constant.file.thumbnailUrl +
        props.thumbnailId +
        '?size=256&tenantUuid=' +
        context.service.auth.getTenant()
      setBackgroundImage(bgImage)
    }
  }, [props.thumbnailId, context])

  const doDownload = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (typeof props.downloadFunction === 'function') {
      props.downloadFunction()
    }
  }

  const { translate } = context.service.filter

  return (
    <div className={'pos-relative' + (props.className ? ' ' + props.className : '')}>
      <iframe
        ref={iframeRef}
        className="pdf-viewer-iframe d-flex align-self-stretch flex-grow"
        // @ts-expect-error - mozallowfullscreen is not in React types but needed for Firefox
        mozallowfullscreen="true"
        allowFullScreen={true}
      />

      {showOverlay && (
        <div className="pdf-viewer-overlay">
          <div className="info-message">
            {showMessage && loaderStatus && (
              <div className="pdf-viewer-message mb10">{translate(loaderStatus, statusParams)}</div>
            )}

            {showLoader && backgroundImage && (
              <div className="pdf-viewer-thumbnail mb10 text-center">
                <div
                  style={{
                    backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none'
                  }}
                />
              </div>
            )}

            {showLoader && (
              <div className="pdf-viewer-loader mb10">
                <div>
                  <div className="fa fa-spin fa-3x fa-fw loader-circle-segment"></div>
                </div>
              </div>
            )}

            {showDownload && typeof props.downloadFunction === 'function' && (
              <div className="pdf-viewer-download mb10">
                <div className="text-center">{translate('PDF_VIEWER_DOWNLOAD_DOCUMENT_MSG')}</div>
                <div className="text-center">
                  <b>
                    <a className="cursor-pointer" onClick={doDownload}>
                      {translate('PDF_VIEWER_DOWNLOAD_DOCUMENT_LINK')}
                    </a>
                  </b>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PdfViewer
